<template>
	<main>
		<div class="ly_row-8 ly_mw-128">
			<UserMenu v-if="displayStore.isDesktop" />
			<div class="ly_col_4 c_responsive">
				<h6>구독 정보</h6>
				<div
					v-if="
						subscriptionDetail?.pay_info_recent &&
						!subscriptionDetail?.subscription_detail.subscription_maintain_yn &&
						route.query.currentTab == 'ING'
					"
					class="sub_text"
				>
					<DangerIcon />
					<p>
						{{ subscriptionDetail?.subscription_detail.subscription_end_date?.split(' ')[0] }} 구독 해지된 상품입니다.
					</p>
				</div>
				<section class="subscription_info_details">
					<div class="c_fieldArea">
						<FieldLayout title="구독상품">
							<InputText
								:value="
									subscriptionDetail?.subscription_detail.pay_info_goods_name === ''
										? '구독권 지급'
										: subscriptionDetail?.subscription_detail.pay_info_goods_name
								"
								readonly
							/>
						</FieldLayout>
						<FieldLayout title="구독대가" class="column">
							<InputText
								v-for="(item, idx) in subscriptionDetail?.master_name"
								:key="idx"
								:value="item.master_name"
								readonly
							/>
						</FieldLayout>
						<FieldLayout
							v-if="
								subscriptionDetail?.subscription_detail.subscription_start_date &&
								subscriptionDetail?.subscription_detail.subscription_end_date
							"
							title="이용기간"
						>
							<InputText
								:value="`${subscriptionDetail?.subscription_detail.subscription_start_date?.split(' ')[0]}~${
									subscriptionDetail?.subscription_detail.subscription_end_date?.split(' ')[0]
								}`"
								readonly
							/>
						</FieldLayout>
						<FieldLayout v-if="subscriptionDetail?.pay_info_recent" title="다음 결제일">
							<InputText
								:value="
									subscriptionDetail?.subscription_detail.subscription_maintain_yn
										? dayjs(subscriptionDetail.subscription_detail.pay_info_next_pay_date).format('YYYY-MM-DD')
										: '정기 결제 해지된 상품'
								"
								readonly
							/>
						</FieldLayout>
						<FieldLayout v-if="subscriptionDetail?.pay_info_recent" title="결제카드사">
							<InputText
								:value="`${subscriptionDetail?.subscription_detail.pay_info_card_company_name ?? '앱 결제'}`"
								readonly
							/>
						</FieldLayout>
						<FieldLayout v-if="subscriptionDetail?.pay_info_recent" title="결제금액">
							<InputText
								:value="`${numberFormat(subscriptionDetail?.subscription_detail.pay_info_goods_price)}원`"
								readonly
							/>
						</FieldLayout>
					</div>
					<div v-if="subscriptionDetail?.pay_info_recent" class="payment_list">
						<div class="payment_list_header">
							<h5>정기 결제 내역</h5>
							<button
								class="c_button"
								@click="$router.push({ path: `/sub/payment-history/${route.params.subscription_pk}` })"
							>
								전체보기<img src="@/assets/img/common/arrow_next.svg" alt="전체보기 아이콘" loading="lazy" />
							</button>
						</div>
						<ul class="history_board payment_board">
							<li v-for="(item, idx) in subscriptionDetail?.pay_info_recent" :key="idx">
								<p>
									<span>결제금액:</span><span>{{ numberFormat(item.pay_info_goods_price) }}원</span>
								</p>
								<p>
									<span>결제일시:</span><span>{{ item.pay_info_datetime }}</span>
								</p>
							</li>
						</ul>
						<div
							v-if="subscriptionDetail?.subscription_detail.subscription_maintain_yn && route.query.currentTab == 'ING'"
							class="button_wrap"
						>
							<button
								class="c_button"
								@click="
									$router.push({
										path: `/sub/cancel/${route.params.subscription_pk}`,
										query: { currentTab: route.query.currentTab },
									})
								"
							>
								구독해지
							</button>
						</div>
					</div>
				</section>
			</div>
		</div>
	</main>
</template>

<script setup lang="ts">
// core
import { computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import dayjs from 'dayjs';

// api
import { useGetMemberSubscriptionDetailApi } from '@/api/mypage';
import { useGetAosPaymentCheckApi, useGetIosPaymentCheckApi } from '@/api/subscribe';
// asset
import DangerIcon from '@/assets/img/common/danger.svg?component';
import FieldLayout from '@/components/form/FieldLayout.vue';
// component
import UserMenu from '@/components/mypage/UserMenu.vue';
// store
import { useDisplayStore } from '@/stores/display';
// util
import { numberFormat } from '@/utils/function/format';

// -----variable-----
const route = useRoute();
const displayStore = useDisplayStore();
// -----api-----
const { data: subscriptionDetail, refetch: refetchSubscriptionDetail } = useGetMemberSubscriptionDetailApi(
	computed(() => {
		return {
			subscription_pk: Number(route.params.subscription_pk),
		};
	})
);
const { mutateAsync: getAosPaymentCheck } = useGetAosPaymentCheckApi();
const { mutateAsync: getIosPaymentCheck } = useGetIosPaymentCheckApi();

// -----composition-----
onMounted(async () => {
	try {
		const agent = navigator.userAgent.toLowerCase();

		const isAndroid = agent.indexOf('android') !== -1;
		const isIOS = agent.indexOf('iphone') !== -1 || agent.indexOf('ipad') !== -1 || agent.indexOf('ipod') !== -1;

		if (isAndroid) {
			try {
				const res = await getAosPaymentCheck({
					subscription_pk: String(route.params.subscription_pk),
				});
				console.log('getAosPaymentCheck 응답:', res);
			} catch (error) {
				console.error('getAosPaymentCheck 에러:', error);
			}
		} else if (isIOS) {
			try {
				const res = await getIosPaymentCheck({
					subscription_pk: String(route.params.subscription_pk),
				});
				console.log('getIosPaymentCheck 응답:', res);
			} catch (error) {
				console.error('getIosPaymentCheck 에러:', error);
			}
		}

		await refetchSubscriptionDetail();
	} catch (error) {
		console.error('전체 프로세스 에러:', error);
	}
});
</script>

<style lang="scss" scoped>
.ly_col_4 {
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 4rem;
}
.column :deep(.field) {
	flex-direction: column;
}
.sub_text {
	display: flex;
	align-items: center;
	gap: 0.2rem;
	color: var(--color-danger);
	font-weight: var(--font-w-light);
	font-size: var(--font-s-mid);
	line-height: 1.9rem;
	svg {
		min-width: 2.2rem;
	}
}
.subscription_info {
	padding-bottom: 8.8rem;
	display: flex;
	flex-direction: column;
	gap: var(--space-xxx-large);
	width: 100%;
	&_details {
		display: flex;
		flex-direction: column;
		gap: 8.8rem;
		.button_wrap {
			width: 100%;
			text-align: center;
			button {
				font-weight: var(--font-w-regular);
				font-size: var(--font-s-mid);
				line-height: 1.79rem;
				color: var(--color-base-light);
				border-bottom: 1px solid var(--color-base-light);
				padding: 0rem;
				height: auto;
				border-radius: 0;
			}
		}
	}
}
.payment {
	&_list {
		display: flex;
		flex-direction: column;
		gap: var(--space-large);
		&_header {
			display: flex;
			justify-content: space-between;
			.c_button {
				font-weight: var(--font-w-medium);
				font-size: var(--font-s-mid);
				line-height: 1.79rem;
				img {
					width: 1.8rem;
				}
			}
		}
	}
	&_board {
		padding-left: 0rem;
		padding-right: 0rem;
		li {
			p {
				display: flex;
				gap: 0.4rem;
				span {
					font-weight: var(--font-w-light);
					font-size: var(--font-s-small);
					line-height: 1.551rem;
					color: var(--color-base-white);
				}
			}
		}
		padding-bottom: 2rem;
	}
}
</style>
